import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as SwapIcon } from '../../../../assets/icons/arrange-circle-icon.svg';
import SelectField from '../../../../shared-components/select-field/SelectField';
import ToggleWithIcons from '../../../../shared-components/toggle-with-icons/ToggleWithIcons';
import { TooltipIcon } from '../../../../shared-components/tooltip/TooltipIcon';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';

/**
 * Interface representing a station's values.
 */
export interface StationValues {
  _id: string;
  name: string;
  location?: string;
  address?: string;
  totalRevenue?: number;
  users?: number;
  consumerCount?: number;
  deviceCount?: number;
  totalPowerConsumed?: number | string;
  energyDispensed?: string;
  electricityCostByUtilityProvider?: string;
  connectors?: number;
  totalConnectors?: number;
  uptime?: string;
  uptimeLast30Days?: string;
  chargingSessions?: number;
  totalChargingSession?: number;
  totalChargingSessions?: number;
  sessionCount?: number;
  avgSessionDuration?: string;
  avgChargingHours?: string;
  healthScore?: number;
  faultCount?: number;
  faultsCount?: number;
  [key: string]: any;
}

/**
 * Interface representing metric configuration.
 */
export interface MetricConfig {
  label: string;
  key: string;
}

/**
 * Select option interface.
 */
interface SelectOption {
  label: string;
  value: string;
}

/**
 * Toggle menu item interface.
 */
interface ToggleItem {
  label: string;
  key: string;
  isActive: boolean;
}

/**
 * LocationCard properties.
 */
interface LocationCardProperties {
  stations: StationValues[];
  metrics: MetricConfig[];
  heading: string;
  differenceOnly?: boolean;
  selectedDevices: string[];
  visibleSelectorCount: number;
  detailCache?: Record<string, StationValues>;
  getFilteredOptions: (currentIndex: number) => SelectOption[];
  onSelectStation: (index: number, value: string) => void;
  onRemoveStation: (index: number) => void;
  onSwapColumns: (first: number, second: number) => void;
  chartToggle: ToggleItem[];
  onClickToggle: (key: string) => void;
}

/**
 * Helper to extract and format a metric value from a station object.
 * @param {StationValues | undefined} station - Station data.
 * @param {string} metricKey - Key of the metric.
 * @returns {string | number} Formatted metric value or fallback '-'.
 */
const getMetricDisplayValue = (station: StationValues | undefined, metricKey: string): string | number => {
  if (!station) {
    return '-';
  }

  let value: any = station[metricKey];

  if (value === undefined || value === null || value === '') {
    switch (metricKey) {
      case 'address':
        value = station.location ?? station.address;
        break;
      case 'consumerCount':
        value = station.users ?? station.consumerCount;
        break;
      case 'totalPowerConsumed':
      case 'electricityCostByUtilityProvider':
      case 'energyDispensed':
        value =
          station.totalPowerConsumed ??
          station.energyDispensed ??
          station.electricityCostByUtilityProvider;
        break;
      case 'totalConnectors':
        value = station.connectors ?? station.totalConnectors;
        break;
      case 'uptimeLast30Days':
        value = station.uptime ?? station.uptimeLast30Days;
        break;
      case 'totalChargingSession':
      case 'chargingSessions':
      case 'totalChargingSessions':
      case 'sessionCount':
        value =
          station.totalChargingSession ??
          station.totalChargingSessions ??
          station.chargingSessions ??
          station.sessionCount;
        break;
      case 'avgChargingHours':
        value = station.avgSessionDuration ?? station.avgChargingHours;
        break;
      case 'faultsCount':
        value = station.faultCount ?? station.faultsCount;
        break;
      default:
        value = station[metricKey];
    }
  }

  if (value === undefined || value === null || value === '') {
    return '-';
  }

  if (typeof value === 'number') {
    if (!Number.isInteger(value)) {
      return String(ToFixedValue(value));
    }
    return String(value);
  }

  return String(value);
};

/**
 * LocationCard - unified comparison grid component rendering content directly.
 * @param {LocationCardProperties} props - The component props.
 * @param {StationValues[]} props.stations - Array of detailed station data.
 * @param {MetricConfig[]} props.metrics - List of metrics to compare.
 * @param {string} props.heading - Comparison card header title.
 * @param {boolean} [props.differenceOnly] - Whether to highlight differences only.
 * @param {string[]} props.selectedDevices - Selected station device IDs.
 * @param {number} props.visibleSelectorCount - Number of visible comparison columns.
 * @param {Record<string, StationValues>} [props.detailCache] - Cache mapping station ID to details.
 * @param {Function} props.getFilteredOptions - Function returning filtered dropdown options.
 * @param {Function} props.onSelectStation - Callback invoked when a station is selected.
 * @param {Function} props.onRemoveStation - Callback invoked when a station is removed.
 * @param {Function} props.onSwapColumns - Callback invoked to swap two station columns.
 * @param {ToggleItem[]} props.chartToggle - Full details vs difference only toggle state.
 * @param {Function} props.onClickToggle - Callback invoked when toggle is clicked.
 * @returns {React.ReactElement} The rendered comparison grid.
 */
function LocationCard({
  stations,
  metrics,
  heading,
  differenceOnly = false,
  selectedDevices,
  visibleSelectorCount,
  detailCache = {},
  getFilteredOptions,
  onSelectStation,
  onRemoveStation,
  onSwapColumns,
  chartToggle,
  onClickToggle,
}: LocationCardProperties): React.ReactElement {
  const { t, i18n } = useTranslation('sidePanel');

  const visibleDevices = useMemo(
    () => selectedDevices.slice(0, visibleSelectorCount),
    [selectedDevices, visibleSelectorCount],
  );

  const activeStations = useMemo(
    () =>
      visibleDevices.map((id) => {
        if (!id) {
          return undefined;
        }
        return detailCache[id] || stations.find((s) => s._id === id);
      }),
    [detailCache, stations, visibleDevices],
  );

  const referenceStation = activeStations[0];
  const gridColumnsClass = `stations-${Math.min(Math.max(visibleSelectorCount, 1), 4)}`;

  return (
    <div className="location-comparison-scroll-wrapper" dir={i18n.dir()}>
      <div className={`location-comparison-grid ${gridColumnsClass}`}>
        {/* ==================== ROW 1: SELECTORS & TOGGLE ==================== */}
        <div className="selector-row-item">
          <div className="location-comparison-toggle">
            <ToggleWithIcons
              leftMenu={chartToggle[0]}
              rightMenu={chartToggle[1]}
              handleClickToggle={onClickToggle}
            />
          </div>
        </div>

        {/* Spacer between Metric Column and Station 1 */}
        <div className="metric-spacer" />

        {visibleDevices.map((deviceId, index) => (
          <React.Fragment key={`selector-group-${index}`}>
            <div className="selector-row-item">
              <div className="select-wrapper position">
                <SelectField
                  options={getFilteredOptions(index)}
                  placeholder={t('selectStation')}
                  selectedValue={deviceId}
                  name={`selectedDevice${index}`}
                  onChangeValue={(_, value) => onSelectStation(index, value)}
                  enableSearch
                />
                {index > 0 && deviceId && (
                  <button
                    type="button"
                    className="clear-selected-station"
                    onClick={(event) => {
                      event.stopPropagation();
                      onRemoveStation(index);
                    }}
                    aria-label={t('remove')}
                    title={t('remove')}>
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M1.5 1.5L10.5 10.5M10.5 1.5L1.5 10.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {index < visibleDevices.length - 1 && (
              <div className="swap-button-wrapper">
                <div className="swap-button">
                  <SwapIcon
                    className="swap-icon"
                    onClick={() => onSwapColumns(index, index + 1)}
                  />
                </div>
              </div>
            )}
          </React.Fragment>
        ))}

        {/* ==================== BODY ROWS (CONTENT-BASED) ==================== */}
        {/* ---------- TITLE / HEADER ROW ---------- */}
        <div className="grid-cell-header metric-header">
          {heading}
          <TooltipIcon
            text={t('stationComparisonTooltipDesc')}
          />
        </div>

        {/* Spacer */}
        <div className="metric-spacer" />

        {activeStations.map((station, index) => (
          <React.Fragment key={`header-station-${index}`}>
            <div className="grid-cell-header station-header">
              {station?.name ? (t(station.name) || station.name) : '-'}
            </div>
            {index < activeStations.length - 1 && <div className="swap-spacer" />}
          </React.Fragment>
        ))}

        {/* ---------- METRIC DATA ROWS ---------- */}
        {metrics.map((metric, metricIndex) => {
          const isLastRow = metricIndex === metrics.length - 1;
          const referenceValue = getMetricDisplayValue(referenceStation, metric.key);

          return (
            <React.Fragment key={`metric-row-${metric.key}`}>
              {/* Left Metric Label Column */}
              <div
                className={`grid-cell metric-label ${isLastRow ? 'last-row' : ''}`}>
                {metric.label}
              </div>

              {/* Spacer between Metric Column and Station 1 */}
              <div className="metric-spacer" />

              {/* Station Value Columns */}
              {activeStations.map((station, stationIndex) => {
                const cellValue = getMetricDisplayValue(station, metric.key);
                const isDifferent =
                  differenceOnly &&
                  stationIndex > 0 &&
                  Boolean(station) &&
                  Boolean(referenceStation) &&
                  cellValue !== referenceValue;

                return (
                  <React.Fragment key={`station-${stationIndex}-metric-${metric.key}`}>
                    <div
                      className={`grid-cell metric-value ${isDifferent ? 'different' : ''} ${isLastRow ? 'last-row' : ''}`}>
                      {cellValue}
                    </div>
                    {stationIndex < activeStations.length - 1 && (
                      <div className="swap-spacer" />
                    )}
                  </React.Fragment>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

LocationCard.defaultProps = {
  differenceOnly: false,
  detailCache: {},
};

export default LocationCard;
